import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Check, Loader2 } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n || 0) + " €";
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function MultiRoomBookingDialog({ rooms = [], open, onOpenChange }) {
  const [arrivee, setArrivee] = useState("");
  const [depart, setDepart] = useState("");
  const [sel, setSel] = useState({}); // id -> {adultes, enfants}
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [terms, setTerms] = useState(false);
  const [contact, setContact] = useState({ nom: "", email: "", telephone: "", message: "", adresse: "", code_postal: "", ville: "", pays: "France" });

  const chambres = rooms.filter((r) => r.type === "chambre");
  const selectedIds = useMemo(() => Object.keys(sel), [sel]);
  const input = "w-full rounded-xl border border-ink-400/25 bg-white px-3.5 py-2.5 text-sm text-ink outline-none focus:border-brand focus:ring-1 focus:ring-brand";
  const label = "text-xs font-medium text-ink-600 mb-1 block";

  const toggle = (id) => setSel((s) => { const n = { ...s }; if (n[id]) delete n[id]; else n[id] = { adultes: 2, enfants: 0 }; return n; });
  const setGuests = (id, k, v) => setSel((s) => {
    const room = chambres.find((r) => r.id === id);
    const cap = Number(room?.capacity) || 6;
    const cur = { ...s[id], [k]: Number(v) };
    if ((cur.adultes || 0) + (cur.enfants || 0) > cap) cur.enfants = Math.max(cap - (cur.adultes || 0), 0);
    return { ...s, [id]: cur };
  });

  const roomsPayload = useCallback(() => selectedIds.map((id) => ({
    accommodation_id: id, adultes: sel[id].adultes, enfants: sel[id].enfants,
  })), [sel, selectedIds]);

  const fetchQuote = useCallback(async () => {
    if (!arrivee || !depart || arrivee >= depart || selectedIds.length === 0) { setQuote(null); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/quote-multi", { arrivee, depart, rooms: roomsPayload() });
      setQuote(data);
    } catch { setQuote(null); }
    finally { setLoading(false); }
  }, [arrivee, depart, selectedIds.length, roomsPayload]);

  useEffect(() => { if (open) fetchQuote(); }, [open, fetchQuote]);

  const submit = async (e) => {
    e.preventDefault();
    if (!arrivee || !depart) { toast.error("Choisissez les dates."); return; }
    if (selectedIds.length === 0) { toast.error("Sélectionnez au moins une chambre."); return; }
    if (!contact.nom || !contact.email) { toast.error("Nom et e-mail requis."); return; }
    if (!terms) { toast.error("Vous devez accepter les conditions de location."); return; }
    const unavail = (quote?.rooms || []).filter((r) => selectedIds.includes(r.accommodation_id) && !r.available);
    if (unavail.length) { toast.error(`Indisponible : ${unavail.map((r) => r.name).join(", ")}`); return; }
    setSending(true);
    try {
      const { data } = await api.post("/bookings/checkout-multi", {
        arrivee, depart, rooms: roomsPayload(), ...contact,
        accept_terms: terms, origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) { toast.error(apiErr(err)); setSending(false); }
  };

  const setC = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream max-w-lg max-h-[90vh] overflow-y-auto" data-testid="multi-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-ink-900">Réserver plusieurs chambres</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-ink-500">Choisissez vos dates, sélectionnez les chambres et payez un seul acompte.</p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Arrivée *</label>
              <input type="date" min={todayStr()} value={arrivee} onChange={(e) => setArrivee(e.target.value)} className={input} data-testid="multi-arrivee" />
            </div>
            <div>
              <label className={label}>Départ *</label>
              <input type="date" min={arrivee || todayStr()} value={depart} onChange={(e) => setDepart(e.target.value)} className={input} data-testid="multi-depart" />
            </div>
          </div>

          <div className="space-y-2" data-testid="multi-rooms">
            <p className="text-xs font-semibold text-ink-700">Chambres</p>
            {chambres.map((r) => {
              const on = !!sel[r.id];
              const q = (quote?.rooms || []).find((x) => x.accommodation_id === r.id);
              const unavailable = arrivee && depart && q && !q.available;
              return (
                <div key={r.id} className={`rounded-xl border p-3 transition-colors ${on ? "border-brand bg-brand-50/50" : "border-ink-400/20"}`} data-testid={`multi-room-${r.id}`}>
                  <label className="flex items-center justify-between gap-2 cursor-pointer">
                    <span className="inline-flex items-center gap-2">
                      <input type="checkbox" checked={on} onChange={() => toggle(r.id)} disabled={unavailable} className="w-4 h-4 accent-brand" data-testid={`multi-check-${r.id}`} />
                      <span className="text-sm font-medium text-ink-900">{r.name}</span>
                      {r.capacity ? <span className="text-[11px] text-ink-400">· {r.capacity} pers. max</span> : null}
                    </span>
                    {unavailable ? <span className="text-xs text-red-600 font-medium">Indisponible</span>
                      : q && on ? <span className="text-xs text-ink-500">acompte {eur(q.deposit)}</span> : null}
                  </label>
                  {on && (
                    <div className="grid grid-cols-2 gap-2 mt-2 pl-6">
                      <div>
                        <label className="text-[11px] text-ink-500">Adultes</label>
                        <select value={sel[r.id].adultes} onChange={(e) => setGuests(r.id, "adultes", e.target.value)} className="w-full rounded-lg border border-ink-400/25 bg-white px-2 py-1 text-sm" data-testid={`multi-adultes-${r.id}`}>
                          {Array.from({ length: Number(r.capacity) || 6 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px] text-ink-500">Enfants</label>
                        <select value={sel[r.id].enfants} onChange={(e) => setGuests(r.id, "enfants", e.target.value)} className="w-full rounded-lg border border-ink-400/25 bg-white px-2 py-1 text-sm" data-testid={`multi-enfants-${r.id}`}>
                          {Array.from({ length: Math.max((Number(r.capacity) || 6) - Number(sel[r.id].adultes) + 1, 1) }, (_, i) => i).map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border border-brand/20 bg-brand-50/40 p-4 space-y-2" data-testid="multi-summary">
            {loading ? <p className="text-sm text-ink-400">Calcul en cours…</p>
              : !quote || selectedIds.length === 0 ? <p className="text-sm text-ink-400">Sélectionnez vos dates et vos chambres.</p>
              : (
                <>
                  <div className="flex items-center justify-between text-sm text-ink-600"><span>{selectedIds.length} chambre(s) · {quote.nights} nuit(s)</span><span className="text-ink-900">{eur(quote.total)}</span></div>
                  <div className="rounded-lg bg-brand text-white px-3 py-2 flex items-center justify-between text-sm font-semibold" data-testid="multi-deposit">
                    <span>Acompte à régler maintenant</span><span>{eur(quote.deposit)}</span>
                  </div>
                  {quote.tourist_tax > 0 && <div className="flex items-center justify-between text-xs text-ink-500"><span>Taxe de séjour (sur place)</span><span>{eur(quote.tourist_tax)}</span></div>}
                </>
              )}
          </div>

          <div><label className={label}>Nom complet *</label><input value={contact.nom} onChange={setC("nom")} required className={input} data-testid="multi-nom" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>E-mail *</label><input type="email" value={contact.email} onChange={setC("email")} required className={input} data-testid="multi-email" /></div>
            <div><label className={label}>Téléphone</label><input value={contact.telephone} onChange={setC("telephone")} className={input} data-testid="multi-tel" /></div>
          </div>
          <div><label className={label}>Adresse</label><input value={contact.adresse} onChange={setC("adresse")} className={input} data-testid="multi-adresse" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className={label}>Code postal</label><input value={contact.code_postal} onChange={setC("code_postal")} className={input} data-testid="multi-cp" /></div>
            <div><label className={label}>Ville</label><input value={contact.ville} onChange={setC("ville")} className={input} data-testid="multi-ville" /></div>
            <div><label className={label}>Pays</label><input value={contact.pays} onChange={setC("pays")} className={input} data-testid="multi-pays" /></div>
          </div>
          <div><label className={label}>Message</label><textarea value={contact.message} onChange={setC("message")} rows={2} className={input} data-testid="multi-message" /></div>

          <label className="flex items-start gap-2 text-xs text-ink-600 cursor-pointer">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="w-4 h-4 accent-brand mt-0.5 shrink-0" data-testid="multi-accept-terms" />
            <span>J'accepte les <a href="/cgv" target="_blank" rel="noopener noreferrer" className="text-brand underline">conditions de location</a> et les <a href="/annulation" target="_blank" rel="noopener noreferrer" className="text-brand underline">conditions d'annulation</a>.</span>
          </label>

          <button type="submit" disabled={sending || loading || !quote || selectedIds.length === 0 || !terms} className="w-full rounded-full bg-brand text-white py-3 font-medium hover:bg-brand-600 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="multi-submit">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            {quote && selectedIds.length ? `Payer l'acompte · ${eur(quote.deposit)}` : "Payer l'acompte"}
          </button>
          <p className="flex items-center gap-1.5 text-[11px] text-ink-500"><Check size={13} className="text-brand" /> Un seul paiement pour toutes les chambres sélectionnées.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
